import React from "react";
import { GlobalModal } from "../../../../../global/GlobalModal";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import {
  datetimeLocalNow,
  mysqlOrIsoToDatetimeLocalMin,
  datetimeLocalStartOfToday,
} from "../../../utils/datetimeLocalBounds";

function purposeLabel(movement, masters) {
  if (!movement?.purpose_id) return "";
  const p = masters?.purposes?.find((x) => String(x.id) === String(movement.purpose_id));
  return p?.purpose_name ?? "";
}

function StatusReadOnly({ statusId }) {
  const statusConfig = {
    1: { label: "OUT", color: "#ef4444", borderColor: "#fca5a5" },
    2: { label: "RETURNED", color: "#10b981", borderColor: "#86efac" },
    3: { label: "PENDING", color: "#f59e0b", borderColor: "#fcd34d" },
    4: { label: "CANCELLED", color: "#6b7280", borderColor: "#d1d5db" },
  };
  const config = statusConfig[statusId] || statusConfig[1];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        backgroundColor: `${config.color}20`,
        color: config.color,
        border: `1px solid ${config.borderColor}`,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      }}
    >
      {config.label}
    </span>
  );
}

const modalScopedCss = `
/* Tighter header / body — only To & Fro modals (gap before form) */
.tf-mv-modal .gff-modal__header {
  padding: 0.625rem 1.5rem 0;
  gap: 0.5rem;
}
.tf-mv-modal .gff-modal__description {
  margin-top: 0.125rem;
}
.tf-mv-modal .gff-modal__body {
  padding: 0.25rem 1.5rem 0.75rem;
}
.tf-mv-modal .form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
  align-items: start;
}
.tf-mv-modal .form-row-full { grid-column: 1 / -1; }
.tf-mv-modal .form-field,
.tf-mv-modal .form-field-full {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.tf-mv-modal .form-field label,
.tf-mv-modal .form-field-full label {
  font-weight: 600;
  font-size: 13px;
  color: #334155;
}
.tf-mv-modal .form-input,
.tf-mv-modal .form-select,
.tf-mv-modal .form-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff;
}
.tf-mv-modal .form-input:read-only {
  background: #f8fafc;
  color: #64748b;
}
.tf-mv-modal .form-textarea { resize: vertical; min-height: 64px; }
.tf-mv-modal .movement-details-form,
.tf-mv-modal .movement-edit-form,
.tf-mv-modal .movement-return-form {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
}
.tf-mv-modal .movement-return-form.movement-return-form--stacked .form-row {
  grid-template-columns: 1fr;
  gap: 6px;
}
.tf-mv-modal .modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}
.tf-mv-modal .delete-confirmation p { margin: 6px 0; line-height: 1.45; }
.tf-mv-modal .delete-confirmation .warning-text {
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
}
.tf-mv-modal .status-display {
  display: flex;
  align-items: center;
  min-height: 28px;
}
.tf-mv-modal .form-hint {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
  line-height: 1.35;
}
@media (max-width: 768px) {
  .tf-mv-modal .form-row { grid-template-columns: 1fr; }
}
`;

export default function ToAndFroMovementModalsSection({
  masters,
  selectedMovement,
  showViewModal,
  showEditModal,
  showReturnModal,
  showDeleteModal,
  movementToDelete,
  loading,
  onViewClose,
  onEditClose,
  onReturnClose,
  onDeleteModalClose,
  onMovementChange,
  onEditSave,
  onReturnSave,
  onConfirmDelete,
}) {
  return (
    <div className="tf-mv-modal">
      <style>{modalScopedCss}</style>
      <GlobalModal
        open={showViewModal}
        onClose={onViewClose}
        title="Movement details"
        description={`Viewing details for movement ${selectedMovement?.movement_code || ""}`}
        size="lg"
        showCloseButton
        closeOnBackdrop
        closeOnEscape
      >
        <div className="movement-details">
          <div className="movement-details-form">
            <div className="form-row">
              <div className="form-field">
                <label>Movement code</label>
                <input
                  type="text"
                  value={selectedMovement?.movement_code || ""}
                  readOnly
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Staff user ID</label>
                <input
                  type="text"
                  value={selectedMovement?.staff_user_id || ""}
                  readOnly
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Date</label>
                <input
                  type="text"
                  value={
                    selectedMovement?.movement_date
                      ? new Date(selectedMovement.movement_date).toLocaleDateString()
                      : ""
                  }
                  readOnly
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Designation</label>
                <input
                  type="text"
                  value={selectedMovement?.designation || ""}
                  readOnly
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row-full">
              <div className="form-field-full">
                <label>Purpose</label>
                <input
                  type="text"
                  value={purposeLabel(selectedMovement, masters)}
                  readOnly
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Place visited</label>
                <input
                  type="text"
                  value={selectedMovement?.place_visited || ""}
                  readOnly
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Status</label>
                <div className="status-display">
                  <StatusReadOnly statusId={selectedMovement?.status_id} />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Out time</label>
                <input
                  type="text"
                  value={selectedMovement?.out_time || ""}
                  readOnly
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>In time</label>
                <input
                  type="text"
                  value={selectedMovement?.in_time || "Not returned"}
                  readOnly
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row-full">
              <div className="form-field-full">
                <label>Remarks</label>
                <textarea
                  value={selectedMovement?.remarks || ""}
                  readOnly
                  className="form-textarea"
                  rows={3}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <GlobalButton onClick={onViewClose} variant="secondary">
            Close
          </GlobalButton>
        </div>
      </GlobalModal>

      <GlobalModal
        open={showEditModal}
        onClose={onEditClose}
        title="Edit movement"
        description={`Editing movement ${selectedMovement?.movement_code || ""}`}
        size="lg"
        showCloseButton
        closeOnBackdrop
        closeOnEscape
      >
        <div className="movement-edit">
          <div className="movement-edit-form">
            <div className="form-row">
              <div className="form-field">
                <label>Movement code</label>
                <input
                  type="text"
                  value={selectedMovement?.movement_code || ""}
                  readOnly
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Movement date</label>
                <input
                  type="date"
                  value={selectedMovement?.movement_date || ""}
                  onChange={(e) => onMovementChange("movement_date", e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Staff user ID</label>
                <input
                  type="number"
                  min={1}
                  value={selectedMovement?.staff_user_id ?? ""}
                  onChange={(e) => onMovementChange("staff_user_id", e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Staff name</label>
                <input
                  type="text"
                  value={selectedMovement?.staff_name || selectedMovement?.name || ""}
                  readOnly
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Designation</label>
                <input
                  type="text"
                  value={selectedMovement?.designation || ""}
                  onChange={(e) => onMovementChange("designation", e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Place visited</label>
                <input
                  type="text"
                  value={selectedMovement?.place_visited || ""}
                  onChange={(e) => onMovementChange("place_visited", e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Purpose</label>
                <select
                  value={selectedMovement?.purpose_id || ""}
                  onChange={(e) => onMovementChange("purpose_id", e.target.value)}
                  className="form-select"
                >
                  <option value="">Select purpose</option>
                  {masters?.purposes?.map((purpose) => (
                    <option key={purpose.id} value={purpose.id}>
                      {purpose.purpose_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Status</label>
                <select
                  value={selectedMovement?.status_id || ""}
                  onChange={(e) => onMovementChange("status_id", e.target.value)}
                  className="form-select"
                >
                  <option value="">Select status</option>
                  {masters?.statuses?.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.status_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Out time</label>
                <input
                  type="datetime-local"
                  value={selectedMovement?.out_time || ""}
                  onChange={(e) => onMovementChange("out_time", e.target.value)}
                  className="form-input"
                  min={datetimeLocalStartOfToday()}
                  max={datetimeLocalNow()}
                />
                <span className="form-hint">Must be today; cannot be in the future.</span>
              </div>
              <div className="form-field">
                <label>In time</label>
                <input
                  type="datetime-local"
                  value={selectedMovement?.in_time || ""}
                  onChange={(e) => onMovementChange("in_time", e.target.value)}
                  className="form-input"
                  min={mysqlOrIsoToDatetimeLocalMin(
                    selectedMovement?.__rawOutTime ?? selectedMovement?.out_time,
                  )}
                  max={datetimeLocalNow()}
                />
                <span className="form-hint">
                  Leave empty if not returned. Not before out time; no future times.
                </span>
              </div>
            </div>
            <div className="form-row-full">
              <div className="form-field-full">
                <label>Remarks</label>
                <textarea
                  value={selectedMovement?.remarks || ""}
                  onChange={(e) => onMovementChange("remarks", e.target.value)}
                  className="form-textarea"
                  rows={3}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <GlobalButton onClick={onEditClose} variant="secondary">
            Cancel
          </GlobalButton>
          <GlobalButton onClick={onEditSave} variant="primary" loading={loading}>
            Save changes
          </GlobalButton>
        </div>
      </GlobalModal>

      <GlobalModal
        open={showReturnModal}
        onClose={onReturnClose}
        title="Return movement"
        description={`Mark movement ${selectedMovement?.movement_code || ""} as returned`}
        size="md"
        showCloseButton
        closeOnBackdrop
        closeOnEscape
      >
        <div className="movement-return">
          <div className="movement-return-form movement-return-form--stacked">
            <div className="form-row">
              <div className="form-field">
                <label>In time</label>
                <input
                  type="datetime-local"
                  value={selectedMovement?.in_time || ""}
                  onChange={(e) => onMovementChange("in_time", e.target.value)}
                  className="form-input"
                  min={mysqlOrIsoToDatetimeLocalMin(
                    selectedMovement?.__rawOutTime ?? selectedMovement?.out_time,
                  )}
                  max={datetimeLocalNow()}
                  required
                />
                <span className="form-hint">Must be on or after out time; cannot be in the future.</span>
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Return remarks</label>
                <textarea
                  value={selectedMovement?.return_remarks || ""}
                  onChange={(e) => onMovementChange("return_remarks", e.target.value)}
                  className="form-textarea"
                  rows={4}
                  placeholder="Add any return remarks…"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <GlobalButton onClick={onReturnClose} variant="secondary">
            Cancel
          </GlobalButton>
          <GlobalButton onClick={onReturnSave} variant="primary" loading={loading}>
            Mark as returned
          </GlobalButton>
        </div>
      </GlobalModal>

      <GlobalModal
        open={showDeleteModal}
        onClose={onDeleteModalClose}
        title="Confirm delete"
        size="sm"
      >
        <div className="delete-confirmation">
          <p>Are you sure you want to delete this movement?</p>
          <p>
            <strong>{movementToDelete?.movement_code}</strong>
          </p>
          <p className="warning-text">This action cannot be undone.</p>
          <div className="modal-actions">
            <GlobalButton onClick={onDeleteModalClose} variant="secondary">
              Cancel
            </GlobalButton>
            <GlobalButton onClick={onConfirmDelete} variant="danger" loading={loading}>
              Delete
            </GlobalButton>
          </div>
        </div>
      </GlobalModal>
    </div>
  );
}
